import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken } from "../lib/api";

const AuthContext = createContext(null);

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem("auth_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading

  const refresh = useCallback(async () => {
    // Show the cached profile immediately so a reload/app-restart never flashes
    // the login screen. The httpOnly auth cookie (same origin as /api) keeps the
    // session valid even when iOS clears localStorage on a PWA restart.
    const cached = readCachedUser();
    if (cached) setUser(cached);

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        localStorage.setItem("auth_user", JSON.stringify(data));
        return data;
      } catch (e) {
        const status = e?.response?.status;
        // Genuine auth failure → really logged out.
        if (status === 401 || status === 403) {
          setToken(null);
          localStorage.removeItem("auth_user");
          setUser(null);
          return null;
        }
        // Transient error (network / 5xx / timeout): wait briefly and retry.
        await new Promise((r) => setTimeout(r, 800));
      }
    }

    // Backend unreachable after retries → keep cached session; only fall to the
    // logged-out state when there is nothing cached to show.
    if (!cached) setUser(null);
    return cached ?? null;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = (token, u) => {
    setToken(token);
    localStorage.setItem("auth_user", JSON.stringify(u));
    setUser(u);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    localStorage.removeItem("auth_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
